import React from 'react';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselPrevious,
    CarouselNext,
    type OnChangeSlideEvent,
} from '@rocco/components/carousel';
import { ReactComponent as ArrowLeft } from '@rocco/icons/svg/arrow/arrow-left.svg';
import { ReactComponent as ArrowRight } from '@rocco/icons/svg/arrow/arrow-right.svg';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { DisplayMode } from '@rocco/types/enum/display-mode';
import { Button as RoccoButton } from '@rocco/components/button';

const _carouselItemVariant = cva(
    'mx-2 max-w-[17.5rem] transition-colors duration-300 first:ml-6 last:mr-6 md:mx-3 md:max-w-[37.125rem] md:first:ml-0 md:last:mr-[30.5625rem]',
    {
        variants: {
            size: {
                default: 'basis-full',
                medium: 'md:max-w-[17rem]',
                small: 'w-44',
            },
        },
        defaultVariants: {
            size: 'default',
        },
    }
);

const _themeVariants = cva('', {
    variants: {
        theme: {
            light: '',
            dark: 'text-white',
        },
    },
});

interface HomeCarouselSectionProps
    extends React.PropsWithChildren,
        VariantProps<typeof _carouselItemVariant>,
        VariantProps<typeof _themeVariants> {
    title?: string;
    showArrows?: boolean;
    description?: string;
    onSlideChange?: (index: number) => void;
    className?: string;
    displayMode: DisplayMode;
    ctaButton?: {
        text: string;
        link: string;
    };
    showLoginModal?: () => void;
    trackClick?: () => void;
}

export const HomeCarouselSection = ({
    title,
    children,
    description,
    size,
    showArrows = true,
    theme = 'light',
    onSlideChange,
    className,
    displayMode,
    ctaButton,
    showLoginModal,
    trackClick,
}: HomeCarouselSectionProps) => {
    const handleSlideChange = (event: OnChangeSlideEvent) => {
        onSlideChange?.(event.currentIndex);
    };

    return (
        <Carousel
            onChangeSlide={handleSlideChange}
            options={{
                align: 'start',
                containScroll: false,
            }}
        >
            <div className="flex w-full flex-col items-start justify-between md:flex-row">
                <div className="w-full pb-8 md:pb-0 md:pt-0">
                    <div className="flex flex-col items-start gap-6 px-6 md:gap-0 md:p-8 md:pl-13 md:pr-20 md:pt-0">
                        <div
                            className={`text-3xl font-normal leading-[110%] tracking-[-0.0875rem] md:w-150 md:text-[4.375rem] ${_themeVariants({ theme })}`}
                            dangerouslySetInnerHTML={{ __html: title || '' }}
                        ></div>
                        <p
                            className={`my-0 pr-12 text-base text-gray-700 md:mb-4 md:mt-14 md:w-[30rem] md:pr-[2.375rem] md:text-5 md:leading-[130%] md:tracking-[0.025rem] ${_themeVariants({ theme })}`}
                        >
                            {description}
                        </p>
                        {ctaButton && (
                            <RoccoButton
                                color="transparent"
                                inverted={true}
                                asChild
                                size={'compact'}
                                shape={'square'}
                                clickAnimation="scale"
                            >
                                <a
                                    href={ctaButton.link}
                                    onClick={(e) => {
                                        trackClick?.();
                                        if (ctaButton.link.includes('/login')) {
                                            e.preventDefault();
                                            showLoginModal?.();
                                        }
                                    }}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        textDecoration: 'underline',
                                        textUnderlineOffset: '0.3rem',
                                        letterSpacing: '0.035rem',
                                        fontSize: '1rem',
                                        fontWeight: '500',
                                        lineHeight: '1.3rem',
                                    }}
                                >
                                    {ctaButton.text}
                                </a>
                            </RoccoButton>
                        )}
                        {showArrows && displayMode === DisplayMode.Desktop && (
                            <div className="flex gap-[1.3rem] md:right-12 md:mt-16">
                                <CarouselPrevious
                                    size={'square64'}
                                    color={
                                        theme === 'dark' ? 'white' : 'glassy'
                                    }
                                    inverted={theme === 'dark'}
                                >
                                    <ArrowLeft className="size-6" />
                                </CarouselPrevious>
                                <CarouselNext
                                    size={'square64'}
                                    color={
                                        theme === 'dark' ? 'white' : 'glassy'
                                    }
                                    inverted={theme === 'dark'}
                                >
                                    <ArrowRight className="size-6" />
                                </CarouselNext>
                            </div>
                        )}
                    </div>
                </div>
                <div className="w-full pl-6 md:w-[57.5%] md:pl-0">
                    <CarouselContent>
                        {React.Children.map(children, (child) => (
                            <CarouselItem
                                className={`${_carouselItemVariant({ size })} ${className}`}
                            >
                                {child}
                            </CarouselItem>
                        ))}
                    </CarouselContent>
                </div>
                {displayMode === DisplayMode.Mobile && showArrows && (
                    <div className="mt-8 flex w-full justify-end gap-2 px-6">
                        <CarouselPrevious size={'square42'} color={'glassy'}>
                            <ArrowLeft className="size-6" />
                        </CarouselPrevious>
                        <CarouselNext size={'square42'} color={'glassy'}>
                            <ArrowRight className="size-6" />
                        </CarouselNext>
                    </div>
                )}
            </div>
        </Carousel>
    );
};
